import {REVERSE} from "data/reverseScores"

// Helper function to detect if all scores are identical
const detectIdenticalScores = (questionAnswers) => {
    const scores = questionAnswers
        .filter(e => e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response)))
        .map(q => q.response);
    
    if (scores.length === 0) return false;
    
    const firstScore = scores[0];
    return scores.every(score => score === firstScore);
};

// Alternative question selection when all scores are identical
const selectQuestionsForIdenticalScores = (questions, questionAnswers, maxRows, minRows) => {
    const validAnswers = questionAnswers.filter(e => 
        e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response))
    );
    
    if (validAnswers.length === 0) return [];
    
    // Get the common score value and first answer for reference
    const commonScore = validAnswers[0].response;
    const firstAnswer = validAnswers[0];
    
    // Create a single general feedback question
    const generalFeedbackQuestion = {
        ...firstAnswer,
        question: "Since all your scores are the same, could you tell us more about your overall experience?",
        fr: "Puisque tous vos scores sont identiques, pourriez-vous nous en dire plus sur votre expérience globale?",
        es: "Dado que todas sus puntuaciones son iguales, ¿podría contarnos más sobre su experiencia general?",
        por: "Como todas as suas pontuações são iguais, poderia nos contar mais sobre sua experiência geral?",
        feedback: "",
        dimension: "general", // General dimension since it's not tied to a specific factor
        factor: "overall_experience", // General factor
        type: 'equal_score_general', // Special type to indicate this is a general equal score question
        response: commonScore, // Keep the common score for reference
        is_general_feedback: true // Flag to help identify this in UI components
    };
    
    // Return only one general question regardless of maxRows/minRows
    return [generalFeedbackQuestion];
};

// Alternative strategy 2: Priority-based selection for identical scores
const selectQuestionsByPriority = (questions, questionAnswers, maxRows, minRows, priorityFactors = []) => {
    const validAnswers = questionAnswers.filter(e => 
        e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response))
    );
    
    const selectedQuestions = [];
    const totalQuestions = maxRows + minRows;
    const usedFactors = new Set();
    
    // First, select from priority factors if specified
    if (priorityFactors.length > 0) {
        for (const priorityFactor of priorityFactors) {
            if (selectedQuestions.length >= totalQuestions) break;
            
            const factorQuestions = validAnswers.filter(q => q.factor === priorityFactor);
            if (factorQuestions.length > 0 && !usedFactors.has(priorityFactor)) {
                selectedQuestions.push(factorQuestions[0]);
                usedFactors.add(priorityFactor);
            }
        }
    }
    
    // Fill remaining slots with diverse factor selection
    const remainingAnswers = validAnswers.filter(q => !usedFactors.has(q.factor));
    const factorGroups = {};
    
    remainingAnswers.forEach(answer => {
        const factor = answer.factor;
        if (!factorGroups[factor]) {
            factorGroups[factor] = [];
        }
        factorGroups[factor].push(answer);
    });
    
    const factorKeys = Object.keys(factorGroups);
    let factorIndex = 0;
    
    while (selectedQuestions.length < totalQuestions && factorKeys.length > 0) {
        const currentFactor = factorKeys[factorIndex % factorKeys.length];
        
        if (factorGroups[currentFactor] && factorGroups[currentFactor].length > 0) {
            selectedQuestions.push(factorGroups[currentFactor].shift());
        }
        
        // Remove factor if no more questions
        if (!factorGroups[currentFactor] || factorGroups[currentFactor].length === 0) {
            factorKeys.splice(factorIndex % factorKeys.length, 1);
            factorIndex = 0;
        } else {
            factorIndex++;
        }
    }
    
    // Map to full question objects
    return selectedQuestions.map(answer => {
        const _ques = questions[answer.ques_order - 1];
        return {
            ...answer,
            fr: _ques?.fr,
            es: _ques?.es,
            por: _ques?.por,
            question: _ques?.question,
            feedback: "",
            dimension: _ques?.dimension,
            factor: _ques?.factor,
            type: 'equal_score_priority'
        };
    });
};

export function RunRules(props) {
    const { 
        questions,
        get_brief_response
    } = props

    const MinQuestions = []
    const MaxQuestions = []

    const MinQuestionSet = []
    const MaxQuestionSet = []

    let maxRows = 0
    let minRows = 0


    if (get_brief_response?.response?.organization_info?.rules) {
        //   console.log("get_brief_response?.response?.organization_info?.rules", get_brief_response?.response?.organization_info?.rules)
        const getMax = get_brief_response?.response?.organization_info?.rules?.filter(ru => ru?.type === "max")?.[0]?.rows > 0
        maxRows = get_brief_response?.response?.organization_info?.rules?.filter(ru => ru?.type === "max")?.[0]?.rows
        const getMin = get_brief_response?.response?.organization_info?.rules?.filter(ru => ru?.type === "min")?.[0]?.rows > 0
        minRows = get_brief_response?.response?.organization_info?.rules?.filter(ru => ru?.type === "min")?.[0]?.rows

        // console.log(getMax)
        // console.log(maxRows)
        // console.log(getMin)
        // console.log(minRows)
        // console.log(questions)
        // console.log("get_brief_response?.answers?.questions", get_brief_response?.answers?.questions)
        if (get_brief_response?.answers?.questions) {
            // const MaxScore = 10
            //  reverse_score = MaxScore + 1 - your_score
            const _revisedValues = Object.values(get_brief_response?.answers?.questions)
                .filter(e => e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response)))
                .map(val => {
                    const base = val?.reverse === true
                        ? { ...val, response: REVERSE[val.response] }
                        : { ...val };
                    const derivedImportance = (val && val.importance !== undefined && val.importance !== null)
                        ? val.importance
                        : (questions?.[val?.ques_order - 1]?.importance ?? 0);
                    return { ...base, importance: derivedImportance };
                })

            if (getMax) {
                const maxValues = _revisedValues.sort((a, b) => {
                    const diff = b.response - a.response;
                    if (diff !== 0) return diff;
                    return (b.importance || 0) - (a.importance || 0);
                });

                maxValues.map(m => {
                    const _ques = questions[m.ques_order - 1]
                     // filter based on factor and dimensions
                     const _found = MaxQuestionSet.filter(mm => mm?.dimension === _ques?.dimension && mm?.factor === _ques?.factor)?.[0]

                     if (!_found) {
                        MaxQuestionSet.push({dimension:_ques?.dimension,
                             factor:  _ques?.factor})
 
                        MaxQuestions.push({
                             ...m,
                             fr: _ques?.fr,
                             es: _ques?.es,
                             por: _ques?.por,
                             question: _ques?.question,
                             feedback: "",
                             dimension: _ques?.dimension,
                             factor: _ques?.factor,
                             type: 'max'
                         })
                    }

                    return null
                })

                // maxValues.slice(0, maxRows).map(m => {
                //     const _ques = questions[m.ques_order - 1]
                //     MaxQuestions.push({
                //         ...m,
                //         fr: _ques?.fr,
                //         question: _ques?.question,
                //         feedback: "",
                //         dimension: _ques?.dimension,
                //         factor: _ques?.factor,
                //         type: 'max'
                //     })
                //     return null
                // })
            }

            if (getMin) {
                const minValues = _revisedValues.sort((a, b) => {
                    const diff = a.response - b.response;
                    if (diff !== 0) return diff;
                    return (b.importance || 0) - (a.importance || 0);
                });
                // console.log(minValues.sort((a,b) => a.response - b.response).slice(0,maxRows));
                
                // console.log(minValues)
               
                minValues.map(m => {
                const _ques = questions[m.ques_order - 1]

                // filter based on factor and dimensions
                const _found = MinQuestionSet.filter(mm => mm?.dimension === _ques?.dimension && mm?.factor === _ques?.factor)?.[0]

                if (!_found) {
                
                    MinQuestionSet.push({dimension:_ques?.dimension,
                        factor:  _ques?.factor})

                    MinQuestions.push({
                        ...m,
                        fr: _ques?.fr,
                        es: _ques?.es,
                        por: _ques?.por,
                        question: _ques?.question,
                        feedback: "",
                        dimension: _ques?.dimension,
                        factor: _ques?.factor,
                        type: 'min'
                    })
                }

                return null
            })
                
            // // console.log(minValues)
            // minValues.slice(0, minRows).map(m => {
            //     const _ques = questions[m.ques_order - 1]
            //     finalMinList.push({
            //         ...m,
            //         fr: _ques?.fr,
            //         question: _ques?.question,
            //         feedback: "",
            //         dimension: _ques?.dimension,
            //         factor: _ques?.factor,
            //         type: 'min'
            //     })
            // return null
            // })
               
            }
        }

        // console.log(finalMaxList)
        // console.log("finalMinList", finalMinList)
        // console.log("MinQuestionSet", MinQuestionSet)
        // console.log("MaxQuestions", MaxQuestions)
        // MaxQuestions.slice(0, maxRows)
        // MinQuestions.slice(0, minRows)
    }
    
  
    // console.log([
    //     ...MaxQuestions, 
    //     ...MinQuestions
    // ])

    return (
        [
            ...MinQuestions.slice(0, minRows),
            ...MaxQuestions.slice(0, maxRows)
        ]
    )
}


export function AuditRunRules( questions, orgRules, questionAnswers,surveyStandards) {
    const MinQuestions = []
    const MaxQuestions = []

    const MinQuestionSet = []
    const MaxQuestionSet = []

    let maxRows = 0
    let minRows = 0

    //Implementation of lowest scores by factor. We fall back to the old method if this fails
    try {
        return FactorScoreRules(questions,orgRules,questionAnswers,surveyStandards)
    } catch (error) {
        console.log(error)
    }
    

    if (orgRules) {
        //   console.log("orgRules", orgRules)
        const getMax = orgRules?.filter(ru => ru?.type === "max")?.[0]?.rows > 0
        maxRows = orgRules?.filter(ru => ru?.type === "max")?.[0]?.rows
        const getMin = orgRules?.filter(ru => ru?.type === "min")?.[0]?.rows > 0
        minRows = orgRules?.filter(ru => ru?.type === "min")?.[0]?.rows



        if (questionAnswers?.questions) {
            // const MaxScore = 10
            //  reverse_score = MaxScore + 1 - your_score
            const _revisedValues = Object.values(questionAnswers?.questions)
                .filter(e => e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response)))
                .map(val => {
                    const base = val?.reverse === true
                        ? { ...val, response: REVERSE[val.response] }
                        : { ...val };
                    const derivedImportance = (val && val.importance !== undefined && val.importance !== null)
                        ? val.importance
                        : (questions?.[val?.ques_order - 1]?.importance ?? 0);
                    return { ...base, importance: derivedImportance };
                })
            if (getMax) {
                const maxValues = _revisedValues.sort((a, b) => {
                    const diff = b.response - a.response;
                    if (diff !== 0) return diff;
                    return (b.importance || 0) - (a.importance || 0);
                });

                maxValues.map(m => {
                    const _ques = questions[m.ques_order - 1]
                     // filter based on factor and dimensions
                     const _found = MaxQuestionSet.filter(mm => mm?.dimension === _ques?.dimension && mm?.factor === _ques?.factor)?.[0]

                     if (!_found) {
                        MaxQuestionSet.push({dimension:_ques?.dimension,
                             factor:  _ques?.factor})
 
                        MaxQuestions.push({
                             ...m,
                             fr: _ques?.fr,
                             es: _ques?.es,
                             por: _ques?.por,
                             question: _ques?.question,
                             feedback: "",
                             dimension: _ques?.dimension,
                             factor: _ques?.factor,
                             type: 'max'
                         })
                    }

                    return null
                })

            }

            if (getMin) {
                const minValues = _revisedValues.sort((a, b) => {
                    const diff = a.response - b.response;
                    if (diff !== 0) return diff;
                    return (b.importance || 0) - (a.importance || 0);
                });
                // console.log(minValues.sort((a,b) => a.response - b.response).slice(0,maxRows));
                
                // console.log(minValues)
               
                minValues.map(m => {
                const _ques = questions[m.ques_order - 1]

                // filter based on factor and dimensions
                const _found = MinQuestionSet.filter(mm => mm?.dimension === _ques?.dimension && mm?.factor === _ques?.factor)?.[0]

                if (!_found) {
                
                    MinQuestionSet.push({dimension:_ques?.dimension,
                        factor:  _ques?.factor})

                    MinQuestions.push({
                        ...m,
                        fr: _ques?.fr,
                        es: _ques?.es,
                        por: _ques?.por,
                        question: _ques?.question,
                        feedback: "",
                        dimension: _ques?.dimension,
                        factor: _ques?.factor,
                        type: 'min'
                    })
                }

                return null
            })
                

               
            }
        }


    }
    

    return (
        [
            ...MinQuestions.slice(0, minRows),
            ...MaxQuestions.slice(0, maxRows)
        ]
    )
}

const get_average_by_factor = (questionAnswers) => {

    const groupedData = {};
    // Add filtering for non-numeric and 'N/A' values
    questionAnswers.filter(e => e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response))).forEach(entry => {
        const key = `${entry.id}_${entry.factor}`;
        if (!groupedData[key]) {
            groupedData[key] = {
                totalResponse: 0,
                count: 0,
                average: 0
            };
        }
        groupedData[key].totalResponse += entry.response;
        groupedData[key].count += 1;
        groupedData[key].average = groupedData[key].totalResponse / groupedData[key].count;
    });


    const arrayData = Object.keys(groupedData).map(key => {
        const [id, factor] = key.split('_').map(Number);
        return {
            id,
            factor,
            ...groupedData[key]
        };
    });
    
    arrayData.sort((a, b) => a.average - b.average);

    return arrayData

}


const get_benchmark_diff_by_factor = (questionAnswers,surveyStandards) => {

    const groupedData = {};

    // Modify the filter to exclude both undefined/null responses and 'N/A' string values
    questionAnswers.filter(e => e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response))).forEach(entry => {
        const key = `${entry.id}_${entry.factor}`;
        if (!groupedData[key]) {
            groupedData[key] = {
                totalResponse: 0,
                count: 0,
                average: 0
            };
        }
       
        
        groupedData[key].totalResponse += entry.response;
        groupedData[key].count += 1;

        const average = groupedData[key].totalResponse / groupedData[key].count;

        let factor = entry.factor

        let dimension = "dimension" in entry ?entry.dimension:entry.id

        const benchmark = surveyStandards?.response?.pillars.filter(s => s.id === dimension && s.factor === factor)[0].average;
        groupedData[key].average = average - benchmark
    });

    const arrayData = Object.keys(groupedData).map(key => {
        const [id, factor] = key.split('_').map(Number);
        return {
            id,
            factor,
            ...groupedData[key]
        };
    });
    
    arrayData.sort((a, b) => a.average - b.average);

    return arrayData

}


function filterQuestions(questions, averages, criteria = 'highest') {
    // Precompute max importance per (id, factor) group for tie-breaking between equal averages
    const maxImportanceByGroup = {};
    questions.forEach(q => {
        const key = `${q.id}_${q.factor}`;
        const imp = (q && q.importance !== undefined && q.importance !== null) ? q.importance : 0;
        if (maxImportanceByGroup[key] === undefined || imp > maxImportanceByGroup[key]) {
            maxImportanceByGroup[key] = imp;
        }
    });

    // Sort averages based on the given criteria; break ties by highest group importance
    const sortedAverages = [...averages].sort((a, b) => {
        const primary = criteria === 'highest' ? (b.average - a.average) : (a.average - b.average);
        if (primary !== 0) return primary;
        const aKey = `${a.id}_${a.factor}`;
        const bKey = `${b.id}_${b.factor}`;
        const aImp = maxImportanceByGroup[aKey] || 0;
        const bImp = maxImportanceByGroup[bKey] || 0;
        const importanceDiff = bImp - aImp;
        if (importanceDiff !== 0) return importanceDiff;
        return 0;
    });

    const result = [];

    for (let avg of sortedAverages) {
        const { factor, id } = avg;

        // Get the relevant questions for this factor-id pairing
        const relevantQuestions = questions.filter(q => q.factor === factor && q.id === id);

        // Sort these relevant questions based on the given criteria, breaking ties by importance
        relevantQuestions.sort((a, b) => {
            const scoreDiff = criteria === 'highest' ? (b.response - a.response) : (a.response - b.response);
            if (scoreDiff !== 0) return scoreDiff;
            const importanceDiff = (b.importance || 0) - (a.importance || 0);
            if (importanceDiff !== 0) return importanceDiff;
            return (a.ques_order || 0) - (b.ques_order || 0);
        });

        if (relevantQuestions.length > 0) {
            result.push(relevantQuestions[0]); // Pick the top question
        }
    }

    return result;
}

const FactorScoreRules = (questions, orgRules, questionAnswers,surveyStandards) => {
    const MinQuestions = []
    const MaxQuestions = []

    const MinQuestionSet = []
    const MaxQuestionSet = []

    let maxRows = 0
    let minRows = 0

    if (orgRules) {

        const getMax = orgRules?.filter(ru => ru?.type === "max")?.[0]?.rows > 0
        maxRows = orgRules?.filter(ru => ru?.type === "max")?.[0]?.rows

        const getMin = orgRules?.filter(ru => ru?.type === "min")?.[0]?.rows > 0
        minRows = orgRules?.filter(ru => ru?.type === "min")?.[0]?.rows
        if (questionAnswers?.questions) {
            const _revisedValues = Object.values(questionAnswers?.questions)
                .filter(e => e.response !== null && e.response !== undefined && e.response !== 'N/A' && !isNaN(Number(e.response)))
                .map(val => {
                    if (val?.reverse === true) {
                        const derivedImportance = (val && val.importance !== undefined && val.importance !== null)
                            ? val.importance
                            : (questions?.[val?.ques_order - 1]?.importance ?? 0);
                        return {
                            ...val,
                            response: REVERSE[val.response],
                            importance: derivedImportance
                        }
                    }
                    const derivedImportance = (val && val.importance !== undefined && val.importance !== null)
                        ? val.importance
                        : (questions?.[val?.ques_order - 1]?.importance ?? 0);
                    return { ...val, importance: derivedImportance }
                })
            // Deduplicate by ques_order; keep the one with the lowest ques_sort_order (stable base)
            const dedupByOrder = new Map();
            _revisedValues.forEach(item => {
                const key = item?.ques_order;
                if (key === undefined || key === null) return;
                const existing = dedupByOrder.get(key);
                if (!existing) {
                    dedupByOrder.set(key, item);
                    return;
                }
                const existingSort = Number(existing?.ques_sort_order ?? Infinity);
                const currentSort = Number(item?.ques_sort_order ?? Infinity);
                if (currentSort < existingSort) {
                    dedupByOrder.set(key, item);
                }
            });
            const _uniqueValues = Array.from(dedupByOrder.values());
            // Check if all scores are identical
            if (detectIdenticalScores(_uniqueValues)) {
                console.log("Detected identical scores - using diversified question selection");
                return selectQuestionsForIdenticalScores(questions, _uniqueValues, maxRows, minRows);
            }

            let factor_averages
            if(surveyStandards && surveyStandards?.response){
                factor_averages = get_benchmark_diff_by_factor(_uniqueValues,surveyStandards)
            }else{
                factor_averages = get_average_by_factor(_uniqueValues)
            }         

            if (getMax) {
                const maxValues = filterQuestions(_uniqueValues, factor_averages, 'highest')

                maxValues.map(m => {
                    const _ques = questions[m.ques_order - 1]
                    // Apply high threshold (ask only if question score strictly greater than threshold)
                    const highThresholdRaw = _ques?.high_follow_up_score
                    const highThreshold = (highThresholdRaw !== null && highThresholdRaw !== undefined) ? Number(highThresholdRaw) : NaN
                    if (!Number.isNaN(highThreshold) && !(Number(m.response) > highThreshold)) {
                        return null
                    }

                    // filter based on factor and dimensions
                    const _found = MaxQuestionSet.filter(mm => mm?.dimension === _ques?.dimension && mm?.factor === _ques?.factor)?.[0]

                    if (!_found) {
                        MaxQuestionSet.push({ dimension: _ques?.dimension, factor: _ques?.factor })

                        MaxQuestions.push({
                            ...m,
                            fr: _ques?.fr,
                            es: _ques?.es,
                            por: _ques?.por,
                            question: _ques?.question,
                            feedback: "",
                            dimension: _ques?.dimension,
                            factor: _ques?.factor,
                            type: 'max'
                        })
                    }

                    return null
                })

            }

            if (getMin) {
                const minValues = filterQuestions(_uniqueValues, factor_averages, 'lowest')

                minValues.map(m => {
                    const _ques = questions[m.ques_order - 1]
                    // Apply low threshold (ask only if question score strictly less than threshold)
                    const lowThresholdRaw = _ques?.low_follow_up_score
                    const lowThreshold = (lowThresholdRaw !== null && lowThresholdRaw !== undefined) ? Number(lowThresholdRaw) : NaN
                    if (!Number.isNaN(lowThreshold) && !(Number(m.response) < lowThreshold)) {
                        return null
                    }

                    // filter based on factor and dimensions
                    const _found = MinQuestionSet.filter(mm => mm?.dimension === _ques?.dimension && mm?.factor === _ques?.factor)?.[0]
                    if (!_found) {
                        MinQuestionSet.push({ dimension: _ques?.dimension, factor: _ques?.factor })

                        MinQuestions.push({
                            ...m,
                            fr: _ques?.fr,
                            es: _ques?.es,
                            por: _ques?.por,
                            question: _ques?.question,
                            feedback: "",
                            dimension: _ques?.dimension,
                            factor: _ques?.factor,
                            type: 'min'
                        })
                    }

                    return null
                })

            }

        }
    }

    return (
        [
            ...MinQuestions.slice(0, minRows),
            ...MaxQuestions.slice(0, maxRows)
        ]
    )


}

export default RunRules;